import { useState } from 'react';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Loader2,
  Plus,
  Minus,
  ArrowLeftRight,
} from 'lucide-react';
import { useAcknowledgeChange } from '../../../../api/curated-integrations.api';
import type { IntegrationChangeDto } from '../../../../api/curated-integrations.types';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface ChangeLogListProps {
  /** UUID of the curated integration these changes belong to. */
  integrationId: string;
  /** Change log entries to display. */
  changes: IntegrationChangeDto[];
  /**
   * Called after a successful acknowledge so the parent can optimistically
   * remove or mark the entry.
   */
  onAcknowledged: (logId: string) => void;
}

interface ChangeTypeConfig {
  label: string;
  className: string;
  icon: React.ReactNode;
}

const CHANGE_TYPE_MAP: Record<IntegrationChangeDto['changeType'], ChangeTypeConfig> = {
  added: {
    label: 'Hinzugefügt',
    className: 'bg-green-100 text-green-800 border-green-200',
    icon: <Plus className="w-3 h-3 mr-1" aria-hidden="true" />,
  },
  removed: {
    label: 'Entfernt',
    className: 'bg-red-100 text-red-800 border-red-200',
    icon: <Minus className="w-3 h-3 mr-1" aria-hidden="true" />,
  },
  schema_changed: {
    label: 'Schema geändert',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: <ArrowLeftRight className="w-3 h-3 mr-1" aria-hidden="true" />,
  },
};

function formatDate(isoStr: string): string {
  try {
    return format(new Date(isoStr), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return isoStr;
  }
}

interface ChangeRowProps {
  integrationId: string;
  change: IntegrationChangeDto;
  onAcknowledged: (logId: string) => void;
}

function ChangeRow({ integrationId, change, onAcknowledged }: ChangeRowProps) {
  const [schemaExpanded, setSchemaExpanded] = useState(false);
  const [optimisticallyAcknowledged, setOptimisticallyAcknowledged] = useState(false);
  const { mutateAsync, isLoading } = useAcknowledgeChange();

  const typeConfig = CHANGE_TYPE_MAP[change.changeType];
  const isAcknowledged = !!change.acknowledgedAt || optimisticallyAcknowledged;
  const hasSchema = change.oldSchema != null || change.newSchema != null;

  const handleAcknowledge = async () => {
    // Optimistic update: mark as acknowledged immediately
    setOptimisticallyAcknowledged(true);
    try {
      await mutateAsync({ integrationId, logId: change.id });
      toast.success(`Änderung für "${change.methodName}" bestätigt.`);
      onAcknowledged(change.id);
    } catch (err: unknown) {
      // Roll back optimistic update on error
      setOptimisticallyAcknowledged(false);
      const message = err instanceof Error ? err.message : 'Fehler beim Bestätigen.';
      toast.error(message);
    }
  };

  return (
    <div
      className={[
        'rounded-lg border p-3 text-sm transition-opacity',
        isAcknowledged ? 'opacity-50 border-gray-200 bg-gray-50' : 'border-gray-200 bg-white',
      ].join(' ')}
      role="listitem"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {/* Change type badge */}
          <Badge
            className={['flex items-center flex-shrink-0', typeConfig.className].join(' ')}
            aria-label={`Änderungstyp: ${typeConfig.label}`}
          >
            {typeConfig.icon}
            {typeConfig.label}
          </Badge>

          <div className="min-w-0">
            {/* Method name */}
            <span className="font-mono font-medium text-blue-700">{change.methodName}</span>
            {/* Date */}
            <div className="text-xs text-gray-400 mt-0.5">
              Erkannt am {formatDate(change.detectedAt)}
              {change.acknowledgedAt && (
                <span className="ml-2 text-green-600">
                  · Bestätigt am {formatDate(change.acknowledgedAt)}
                </span>
              )}
            </div>

            {/* Schema toggle */}
            {hasSchema && (
              <button
                type="button"
                onClick={() => setSchemaExpanded((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-1.5 transition-colors"
                aria-expanded={schemaExpanded}
                aria-label={schemaExpanded ? 'Schema ausblenden' : 'Schema anzeigen'}
              >
                {schemaExpanded ? (
                  <ChevronUp className="w-3 h-3" aria-hidden="true" />
                ) : (
                  <ChevronDown className="w-3 h-3" aria-hidden="true" />
                )}
                Schema {schemaExpanded ? 'ausblenden' : 'anzeigen'}
              </button>
            )}
          </div>
        </div>

        {/* Acknowledge button */}
        {!isAcknowledged ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAcknowledge}
            disabled={isLoading}
            aria-label={`Änderung für Methode ${change.methodName} bestätigen`}
            className="flex-shrink-0 h-7 text-xs"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCircle className="w-3.5 h-3.5 mr-1 text-green-600" aria-hidden="true" />
            )}
            Bestätigen
          </Button>
        ) : (
          <span
            className="text-xs text-green-600 flex items-center gap-1 flex-shrink-0"
            aria-label="Bereits bestätigt"
          >
            <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" />
            Bestätigt
          </span>
        )}
      </div>

      {/* Schema diff panels */}
      {schemaExpanded && hasSchema && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {change.oldSchema != null && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">Vorher</div>
              <pre className="text-xs bg-gray-100 rounded p-2 overflow-auto max-h-40 text-gray-700 whitespace-pre-wrap break-all">
                {JSON.stringify(change.oldSchema, null, 2)}
              </pre>
            </div>
          )}
          {change.newSchema != null && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">Nachher</div>
              <pre className="text-xs bg-gray-100 rounded p-2 overflow-auto max-h-40 text-gray-700 whitespace-pre-wrap break-all">
                {JSON.stringify(change.newSchema, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * List of integration method change log entries.
 * Each row has an Acknowledge button with optimistic update.
 */
export function ChangeLogList({
  integrationId,
  changes,
  onAcknowledged,
}: ChangeLogListProps) {
  if (changes.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">Keine ausstehenden Änderungen.</p>
    );
  }

  return (
    <div className="space-y-2" role="list" aria-label="Änderungsprotokoll">
      {changes.map((change) => (
        <ChangeRow
          key={change.id}
          integrationId={integrationId}
          change={change}
          onAcknowledged={onAcknowledged}
        />
      ))}
    </div>
  );
}
